import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaClock, FaTimes, FaFilter, FaChartBar } from 'react-icons/fa';
import api from '../../api/axios';

const STATUS_STYLE = {
  completed: { label: 'Confirmé',   color: 'text-green-400',  bg: 'rgba(34,197,94,0.1)',  border: 'rgba(34,197,94,0.3)'  },
  pending:   { label: 'En attente', color: 'text-yellow-400', bg: 'rgba(234,179,8,0.1)',  border: 'rgba(234,179,8,0.3)'  },
  failed:    { label: 'Échoué',     color: 'text-red-400',    bg: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.3)'  },
};

const TYPE_LABEL = (plan) => {
  if (!plan) return { label: '—', icon: '❓' };
  if (plan === 'monthly')        return { label: 'Premium Mensuel', icon: '👑' };
  if (plan === 'yearly')         return { label: 'Premium Annuel',  icon: '👑' };
  if (plan === 'coins_starter')  return { label: 'Pack Starter',    icon: '🪙' };
  if (plan === 'coins_standard') return { label: 'Pack Standard',   icon: '🪙' };
  if (plan === 'coins_pro')      return { label: 'Pack Pro',        icon: '🪙' };
  if (plan === 'coins_mega')     return { label: 'Pack Méga',       icon: '🪙' };
  return { label: plan, icon: '💳' };
};

const fcfa = (v) => `${Number(v || 0).toLocaleString()} FCFA`;

export default function AdminCoins() {
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterPeriode, setFilterPeriode] = useState('');
  const [page, setPage]                 = useState(1);
  const [pagination, setPagination]     = useState(null);

  const load = useCallback((p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 20 });
    if (filterStatus)  params.set('status', filterStatus);
    if (filterType)    params.set('type', filterType);
    if (filterPeriode) params.set('periode', filterPeriode);
    api.get(`/admin/transactions?${params}`)
      .then(({ data }) => {
        setTransactions(data.transactions || []);
        setTotals(data.totals || null);
        setPagination(data.pagination || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filterStatus, filterType, filterPeriode]);

  useEffect(() => { setPage(1); load(1); }, [load]);

  const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }) : '—';

  return (
    <div className="flex flex-col gap-6">

      {/* Titre */}
      <div>
        <h1 className="text-2xl font-black text-white uppercase tracking-widest"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          <FaChartBar className="inline mr-2 mb-1 text-qfl-gold" size={20} />
          Transactions
        </h1>
        <p className="text-white/40 text-sm mt-1">Paiements PaiementPro — encaissements, frais, reversements</p>
      </div>

      {/* Filtre période (affecte les totaux ET la liste) */}
      <div className="flex gap-2 flex-wrap">
        {[
          { v: '',      l: 'Tout' },
          { v: 'today', l: "Aujourd'hui" },
          { v: '7d',    l: '7 jours' },
          { v: '30d',   l: '30 jours' },
          { v: 'month', l: 'Ce mois' },
        ].map(({ v, l }) => (
          <button key={v} onClick={() => setFilterPeriode(v)}
            className="px-4 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all"
            style={{
              background: filterPeriode === v ? 'rgba(255,184,28,0.15)' : 'rgba(255,255,255,0.04)',
              border: filterPeriode === v ? '1px solid rgba(255,184,28,0.4)' : '1px solid rgba(255,255,255,0.08)',
              color: filterPeriode === v ? '#FFB81C' : 'rgba(255,255,255,0.4)',
            }}>
            {l}
          </button>
        ))}
      </div>

      {/* ── Bloc financier ──────────────────────────────────────────── */}
      {totals && (
        <div className="flex flex-col gap-3">

          {/* Ligne 1 : Brut encaissé */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Brut encaissé (total)', value: fcfa(totals.brut_total),   color: '#FFB81C', sub: `${totals.completed} paiements confirmés` },
              { label: 'dont Abonnements 👑',   value: fcfa(totals.brut_premium), color: '#FFB81C', sub: `${totals.premium_count} abonnements` },
              { label: 'dont Packs pièces 🪙',  value: fcfa(totals.brut_coins),   color: '#60a5fa', sub: `${totals.coins_count} packs` },
            ].map(({ label, value, color, sub }) => (
              <div key={label} className="rounded-2xl p-4"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="text-white/40 text-xs uppercase tracking-wider mb-1">{label}</div>
                <div className="font-black text-lg" style={{ color, fontFamily: "'Rajdhani', sans-serif" }}>{value}</div>
                <div className="text-white/25 text-xs mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          {/* Ligne 2 : Frais opérateur */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Frais opérateur 5% (total)', value: fcfa(totals.frais_total),   color: '#f87171' },
              { label: 'Frais sur abonnements',       value: fcfa(totals.frais_premium), color: '#f87171' },
              { label: 'Frais sur packs pièces',      value: fcfa(totals.frais_coins),   color: '#f87171' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-2xl p-4"
                style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <div className="text-white/40 text-xs uppercase tracking-wider mb-1">{label}</div>
                <div className="font-black text-lg" style={{ color, fontFamily: "'Rajdhani', sans-serif" }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Ligne 3 : Net reversé */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'NET reversé (total)',    value: fcfa(totals.net_total),   color: '##003DA5' },
              { label: 'Net abonnements',        value: fcfa(totals.net_premium), color: '##003DA5' },
              { label: 'Net packs pièces',       value: fcfa(totals.net_coins),   color: '##003DA5' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-2xl p-4"
                style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)' }}>
                <div className="text-white/40 text-xs uppercase tracking-wider mb-1">{label}</div>
                <div className="font-black text-xl" style={{ color, fontFamily: "'Rajdhani', sans-serif" }}>{value}</div>
              </div>
            ))}
          </div>

        </div>
      )}

      {/* Filtres liste */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex items-center gap-1 text-white/40 text-sm">
          <FaFilter size={11} /> Filtrer :
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="form-input py-1.5 text-sm">
          <option value="">Tous les statuts</option>
          <option value="completed">Confirmés</option>
          <option value="pending">En attente</option>
          <option value="failed">Échoués</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
          className="form-input py-1.5 text-sm">
          <option value="">Tous les types</option>
          <option value="premium">Abonnements Premium</option>
          <option value="coins">Packs de pièces</option>
        </select>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 border-4 border-qfl-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-10 text-white/30 text-sm">Aucune transaction trouvée</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-green-900">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="border-b border-green-900 bg-qfl-card">
                  {['Joueur', 'Type', 'Brut', '5% opér.', 'Net', 'Statut', 'Date'].map((h) => (
                    <th key={h} className="text-left px-3 py-3 text-white/50 font-bold uppercase tracking-wider text-xs"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, i) => {
                  const s    = STATUS_STYLE[t.status] || STATUS_STYLE.pending;
                  const type = TYPE_LABEL(t.plan);
                  const fee  = Math.round(t.amount * 0.05);
                  const net  = t.amount - fee;
                  return (
                    <motion.tr key={t.id}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.02 }}
                      className="border-b border-green-900/40 hover:bg-white/5 transition-colors">

                      <td className="px-3 py-3">
                        <div className="font-bold text-white text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                          {t.pseudo}
                        </div>
                        <div className="text-white/30 text-xs">{t.email}</div>
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <span>{type.icon}</span>
                        <span className="text-white/60 text-xs ml-1">{type.label}</span>
                        {t.coins_amount > 0 && (
                          <div className="text-white/30 text-xs">🪙 {Number(t.coins_amount).toLocaleString()} pièces</div>
                        )}
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="font-black text-white/80" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                          {Number(t.amount).toLocaleString()}
                        </span>
                        <span className="text-white/30 text-xs ml-1">FCFA</span>
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="text-red-400 font-bold text-xs">-{fee.toLocaleString()} FCFA</span>
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="font-black text-green-400" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                          {net.toLocaleString()}
                        </span>
                        <span className="text-white/30 text-xs ml-1">FCFA</span>
                      </td>

                      <td className="px-3 py-3">
                        <span className="text-xs font-bold px-2 py-1 rounded-full border"
                          style={{ color: s.color, background: s.bg, borderColor: s.border }}>
                          {t.status === 'completed' && <FaCheck size={9} className="inline mr-1" />}
                          {t.status === 'pending'   && <FaClock size={9} className="inline mr-1" />}
                          {t.status === 'failed'    && <FaTimes size={9} className="inline mr-1" />}
                          {s.label}
                        </span>
                      </td>

                      <td className="px-3 py-3 text-white/35 text-xs whitespace-nowrap">
                        {fmt(t.created_at)}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/40">{pagination.total} transaction(s)</span>
              <div className="flex gap-2">
                <button disabled={page <= 1}
                  onClick={() => { const p = page - 1; setPage(p); load(p); }}
                  className="px-3 py-1.5 rounded-lg border border-white/10 text-white/50 disabled:opacity-30 hover:bg-white/5">
                  Préc.
                </button>
                <span className="px-3 py-1.5 text-white/60">{page} / {pagination.totalPages}</span>
                <button disabled={page >= pagination.totalPages}
                  onClick={() => { const p = page + 1; setPage(p); load(p); }}
                  className="px-3 py-1.5 rounded-lg border border-white/10 text-white/50 disabled:opacity-30 hover:bg-white/5">
                  Suiv.
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


